import { create } from 'zustand';
import type { SimulationTest, MessageRule, CoordinateConfig } from '@k-ucap/shared';
import { createRepositories } from '@/infrastructure/repositories/RepositoryFactory.js';

export interface SimulationTestState {
  tests: SimulationTest[];
  selectedTestId: string | null;
  isLoading: boolean;
  error: string | null;

  loadTests: (projectId: string) => Promise<void>;
  selectTest: (id: string | null) => void;
  createTest: (projectId: string, userId: string, environment: string) => Promise<SimulationTest>;
  updateTest: (test: SimulationTest) => Promise<void>;
  saveCurrentTest: () => Promise<void>;
  duplicateTest: (id: string) => Promise<void>;
  deleteTest: (id: string) => Promise<void>;
  addMessageRule: (testId: string, messageType: number, definitionId?: string) => void;
  updateMessageRule: (testId: string, ruleId: string, updates: Partial<MessageRule>) => void;
  removeMessageRule: (testId: string, ruleId: string) => void;
  updateLocationConfig: (testId: string, config: CoordinateConfig) => void;
  clearError: () => void;
}

export const useSimulationTestStore = create<SimulationTestState>((set, get) => {
  const { simulationTestRepository } = createRepositories();

  const updateLocalTest = (updated: SimulationTest) => {
    set({
      tests: get().tests.map((t) => (t.id === updated.id ? updated : t)),
      error: null,
    });
  };

  return {
    tests: [],
    selectedTestId: null,
    isLoading: false,
    error: null,

    loadTests: async (projectId) => {
      set({ isLoading: true, error: null });
      try {
        const tests = await simulationTestRepository.findByProjectId(projectId);
        set({ tests, isLoading: false });
      } catch (err) {
        set({ error: (err as Error).message, isLoading: false });
      }
    },

    selectTest: (id) => set({ selectedTestId: id }),

    createTest: async (projectId, userId, environment) => {
      const newTest: SimulationTest = {
        id: `sim-${Date.now()}`,
        projectId,
        name: '새 시험',
        description: '',
        category: 'Flight',
        tags: [],
        environment,
        vehicleIds: [],
        messageRules: [],
        locationConfig: {
          defaultCoordinate: {
            mode: 'CENTER_RADIUS',
            centerLat: 37.389,
            centerLon: 126.647,
            radiusKm: 10,
          },
        },
        executionConfig: {
          recordMode: 'SUMMARY',
          sampleRate: 10,
          maxConcurrentRules: 5,
          stopOnError: false,
        },
        createdBy: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await simulationTestRepository.save(newTest);
      set({ tests: [...get().tests, newTest], selectedTestId: newTest.id });
      return newTest;
    },

    updateTest: async (test) => {
      const previousTests = get().tests;
      const updated: SimulationTest = { ...test, updatedAt: new Date().toISOString() };
      updateLocalTest(updated);
      try {
        await simulationTestRepository.save(updated);
      } catch (err) {
        set({ tests: previousTests, error: (err as Error).message });
        throw err;
      }
    },

    saveCurrentTest: async () => {
      const { selectedTestId, tests } = get();
      if (!selectedTestId) return;
      const test = tests.find((t) => t.id === selectedTestId);
      if (!test) return;
      set({ error: null });
      try {
        await simulationTestRepository.save(test);
      } catch (err) {
        set({ error: (err as Error).message });
        throw err;
      }
    },

    duplicateTest: async (id) => {
      const duplicated = await simulationTestRepository.duplicate(id);
      if (!duplicated) return;
      set({ tests: [...get().tests, duplicated], selectedTestId: duplicated.id });
    },

    deleteTest: async (id) => {
      await simulationTestRepository.delete(id);
      set({
        tests: get().tests.filter((t) => t.id !== id),
        selectedTestId: get().selectedTestId === id ? null : get().selectedTestId,
      });
    },

    addMessageRule: (testId, messageType, definitionId) => {
      const test = get().tests.find((t) => t.id === testId);
      if (!test) return;
      const newRule: MessageRule = {
        id: `rule-${Date.now()}`,
        messageType,
        messageDefinitionId: definitionId,
        enabled: true,
        payloadTemplate: {},
        fieldRules: {},
        generationMode: 'PERIODIC',
        intervalMs: 1000,
        count: 100,
        startDelayMs: 0,
        randomization: false,
        faultInjection: false,
        order: test.messageRules.length,
      };
      const updated: SimulationTest = {
        ...test,
        messageRules: [...test.messageRules, newRule],
        updatedAt: new Date().toISOString(),
      };
      updateLocalTest(updated);
    },

    updateMessageRule: (testId, ruleId, updates) => {
      const test = get().tests.find((t) => t.id === testId);
      if (!test) return;
      const updated: SimulationTest = {
        ...test,
        messageRules: test.messageRules.map((r) => (r.id === ruleId ? { ...r, ...updates } : r)),
        updatedAt: new Date().toISOString(),
      };
      updateLocalTest(updated);
    },

    removeMessageRule: (testId, ruleId) => {
      const test = get().tests.find((t) => t.id === testId);
      if (!test) return;
      const updated: SimulationTest = {
        ...test,
        messageRules: test.messageRules.filter((r) => r.id !== ruleId),
        updatedAt: new Date().toISOString(),
      };
      updateLocalTest(updated);
    },

    updateLocationConfig: (testId, config) => {
      const test = get().tests.find((t) => t.id === testId);
      if (!test) return;
      const updated: SimulationTest = {
        ...test,
        locationConfig: { ...test.locationConfig, defaultCoordinate: config },
        updatedAt: new Date().toISOString(),
      };
      updateLocalTest(updated);
    },

    clearError: () => set({ error: null }),
  };
});

export default useSimulationTestStore;
